// Extracts code and phone number from a given string
type TFormatePhoneNumberInput = {
  phone?: string | number;
  code?: string | number;
  formValue?: string;
  displayVersion?: string;
};

export type TFormattedPhoneNumber = { phone: string; code: string; formValue: string; display: string };

const extractCodeAndPhone = (str?: string) => {
  if (!str) return { code: '', phone: '' };

  let code = '';
  let phone = '';

  if (str.includes('_')) {
    // If the string contains an underscore, split by underscore to extract code and phone
    [code, phone] = str.split('_');
  } else if (str.includes('-')) {
    // If the string contains a hyphen, split by hyphen
    const parts = str.split('-');
    phone = parts.pop() || ''; // Last part is phone
    code = parts.join('-'); // Rest is code
  } else {
    // If no separator is found, consider the whole string as the phone number
    phone = str;
  }

  return { code, phone };
};

// Extracts code and phone from the display format (e.g., +44-998987987)
const extractFromDisplay = (display: string) => {
  if (!display.startsWith('+')) return { code: '', phone: display };

  // Extract code and phone from the display
  const parts = display.split('-');
  const phone = parts?.length ? parts[parts.length - 1] : ''; // last part is phone number
  const code = parts?.length ? parts.slice(0, parts.length - 1)?.join('-') : ''; // the rest is code

  return { code, phone };
};

// Formats a phone number object with optional code, phone, formValue, and display properties
export const formatPhoneNumber = (obj: TFormatePhoneNumberInput): TFormattedPhoneNumber => {
  let phone = obj.phone?.toString() || '';
  let code = obj.code?.toString() || '';
  let formValue = obj.formValue?.toString() || '';
  let display = obj.displayVersion || '';

  // Handle cases where 'display' is provided
  if (display) {
    const extracted = extractFromDisplay(display);
    code = code || extracted.code;
    phone = phone || extracted.phone;
  }

  // Handle cases where 'formValue' is not provided
  if (!formValue) {
    if (phone && code) {
      formValue = `${code}_${phone}`;
    } else if (phone) {
      formValue = phone;
    } else if (code) {
      formValue = `${code}_`;
    }
  } else {
    // If 'formValue' is provided, extract code and phone from it
    const extracted = extractCodeAndPhone(formValue);
    code = code || extracted.code;
    phone = phone || extracted.phone;
  }

  // Normalize code by removing any '+' sign
  if (code.includes('+')) {
    code = code.replace('+', '');
  }

  // Reconstruct 'formValue' if needed
  if (!formValue && (code || phone)) {
    formValue = code && phone ? `${code}_${phone}` : phone;
  }

  // Create the formatted display version of the phone number
  display = code ? `+${code.replace('_', '')}${phone && '-'}${phone}` : phone;

  return { phone, code, formValue, display };
};
